if (!customElements.get('pdp-canvas-modal')) {
  class PDPCanvasModal extends HTMLElement {
    constructor() {
      super();
      this.handleOpen = this.handleOpen.bind(this);
      this.handleClose = this.handleClose.bind(this);
      this.handleKeyDown = this.handleKeyDown.bind(this);
      this.openBtnId = null;
      this.closeBtnId = null;
      this.dialogId = null;
    }

    connectedCallback() {
      this.openBtnId = this.getAttribute('data-open-button');
      this.closeBtnId = this.getAttribute('data-close-button');
      this.dialogId = this.getAttribute('data-dialog-id');
      this.closeBtn = this.querySelector(`#${this.closeBtnId}`);
      this.openBtn = document.getElementById(this.openBtnId);
      this.modal = this.querySelector(`#${this.dialogId}`);

      if (this.openBtn) {
        this.openBtn.addEventListener('click', this.handleOpen);
      }

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', this.handleClose);
      }

      if (this.modal) {
        this.modal.addEventListener('keydown', this.handleKeyDown);
      }

      // Restore the modal ONLY after a genuine reload — see getModalOpenState().
      if (this.getModalOpenState()) {
        this.handleOpen();
      }
    }

    disconnectedCallback() {
      if (this.openBtn) {
        this.openBtn.removeEventListener('click', this.handleOpen);
      }

      if (this.closeBtn) {
        this.closeBtn.removeEventListener('click', this.handleClose);
      }

      if (this.modal) {
        this.modal.removeEventListener('keydown', this.handleKeyDown);
      }
    }

    handleOpen() {
      if (this.modal && typeof this.modal.show === 'function') {
        if (this.modal.hasAttribute('open')) return; // already open — never re-show
        this.modal.show();
        this.handleBodyScroll(true);
        this.modal.focus(); // Ensure modal receives keyboard events
        this.setModalOpenState(true);
        this.publishModalToggleEvent(true);
      }
    }

    handleClose() {
      if (this.modal && typeof this.modal.close === 'function') {
        this.modal.close();
        this.handleBodyScroll(false);
        this.setModalOpenState(false);
        this.publishModalToggleEvent(false);
      }
    }

    /**
     * Publishes a single custom event for modal open/close actions.
     * @param {boolean} open - True if modal is open, false if closed.
     */
    publishModalToggleEvent(open) {
      this.dispatchEvent(
        new CustomEvent('canvas-modal-toggle', {
          detail: { dialogId: this.dialogId, isOpen: open },
          bubbles: true,
          composed: true,
        }),
      );
    }

    /**
     * Sets or removes the modal open state in localStorage for this dialogId.
     * @param {boolean} isOpen
     */
    setModalOpenState(isOpen) {
      const modalKey = `modal-open-${this.dialogId}`;
      try {
        if (isOpen) {
          localStorage.setItem(modalKey, 'true');
        } else {
          localStorage.removeItem(modalKey);
        }
      } catch (e) {
        /* private mode / quota — restoring is a nicety, never break opening over it */
      }
    }

    /**
     * True only when this modal should be re-opened automatically.
     *
     * The flag exists so a page RELOAD in the middle of configuring does not throw the
     * shopper out of the modal. But localStorage outlives the page: the flag is only
     * cleared by an explicit close, so leaving with the modal open (navigating to
     * another product, following a link, closing the tab) left it set — and then the
     * modal re-opened BY ITSELF on the next visit to any product, with the lazily
     * loaded wizard booting into it afterwards. That is the "it opens and then reloads
     * the whole modal" glitch (diagnosed 2026-08-05).
     *
     * Fix: restore only when the page was actually RELOADED. A fresh navigation to the
     * product clears the flag and opens nothing. Reload keeps the old behaviour intact.
     */
    getModalOpenState() {
      const modalKey = `modal-open-${this.dialogId}`;
      let flagged = false;
      try {
        flagged = localStorage.getItem(modalKey) === 'true';
      } catch (e) {
        return false;
      }
      if (!flagged) return false;

      let isReload = false;
      try {
        const nav = performance.getEntriesByType('navigation')[0];
        if (nav && nav.type) {
          isReload = nav.type === 'reload';
        } else if (performance.navigation) {
          // Older WebKit: 1 === TYPE_RELOAD
          isReload = performance.navigation.type === 1;
        }
      } catch (e) {
        isReload = false;
      }

      if (!isReload) {
        // Arrived fresh with a stale flag — drop it so it cannot fire again later.
        this.setModalOpenState(false);
        return false;
      }
      return true;
    }

    handleKeyDown(e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        this.handleClose();
      }
    }

    handleBodyScroll(lock = false) {
      const body = document.body;
      body.classList.toggle('modal-open', lock);
    }
  }

  customElements.define('pdp-canvas-modal', PDPCanvasModal);
}